/**
 * Manages state for case detail drawer.
 * Supports lookup across active and new cases.
 */
import { useCallback, useMemo, useState } from 'react';

import { ActiveCase, CaseType, NewCase } from '#entities';

import { useQueryActiveCases, useQueryNewCases } from '../../api';

export const useCaseSelection = () => {
  const { data: newCases } = useQueryNewCases();
  const { data: activeCases } = useQueryActiveCases();
  const [open, setOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<ActiveCase | NewCase | null>(
    null,
  );
  const [selectedCaseType, setSelectedCaseType] = useState<CaseType | null>(
    null,
  );

  const handleSelectCaseById = useCallback(
    (id: string) => {
      const foundActive = activeCases.find(c => c.id === id);
      const foundNew = newCases.find(c => c.id === id);

      if (foundActive) {
        setSelectedCase(foundActive);
        setSelectedCaseType(CaseType.ActiveCase);
        setOpen(true);
      } else if (foundNew) {
        setSelectedCase(foundNew);
        setSelectedCaseType(CaseType.NewCase);
        setOpen(true);
      }
    },
    [activeCases, newCases],
  );

  const handleSelectCaseByLabel = useCallback(
    (label: string) => {
      const foundActive = activeCases.find(c => c.label === label);
      const foundNew = newCases.find(c => c.label === label);

      if (foundActive) {
        setSelectedCase(foundActive);
        setSelectedCaseType(CaseType.ActiveCase);
        setOpen(true);
      } else if (foundNew) {
        setSelectedCase(foundNew);
        setSelectedCaseType(CaseType.NewCase);
        setOpen(true);
      }
    },
    [activeCases, newCases],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedCase(null);
    setSelectedCaseType(null);
  }, []);

  return useMemo(
    () => ({
      open,
      selectedCase,
      selectedCaseType,
      handleSelectCaseById,
      handleSelectCaseByLabel,
      handleClose,
    }),
    [
      open,
      selectedCase,
      selectedCaseType,
      handleSelectCaseById,
      handleSelectCaseByLabel,
      handleClose,
    ],
  );
};
